import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { MovePlanComponent } from './move-plan.component';
import { PlanMapListComponent } from './plan-map-list.component';
import { useState } from 'react';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { planApiKeyStore } from '../../../api/plan-api-key-store';
import { useSaveData } from '../../../hooks/useSaveData';
import { useAuthorize } from '../../../hooks/use-authorize';
import { UserPermissionName } from '../../../models/user-permission-name';

interface MovePlanDialog {
  planData: any;
  onClosed: () => void;
}

export const MovePlanDialog = ({ planData, onClosed }: MovePlanDialog) => {
  const form = useReactHookForm({
    defaultValues: {
      planID: planData?.planID,
      planName: planData?.planName,
    },
  });

  const [newMappedData, setNewMappedData] = useState<any[]>([]);

  const { isLoading: isMovingPlanLoading, mutateAsync } = useSaveData(planApiKeyStore.mapPlans(), () =>
    planApiKeyStore.getPlans({ customerID: planData?.customerID, officeID: planData?.officeID })
  );

  const retriveIds = () => {
    const officeIDs: any = [];
    const projectIDs: any = [];
    const unitIDs: any = [];

    Array.isArray(newMappedData) &&
      newMappedData?.forEach((office: any) => {
        if (office.isAdded) {
          officeIDs.push(office.officeID);
        }
        Array.isArray(office?.planProjectMappings) &&
          office.planProjectMappings?.forEach((project: any) => {
            if (project.isAdded) {
              projectIDs.push(project.projectID);
            }
            Array.isArray(project?.planUnitMappings) &&
              project.planUnitMappings?.forEach((unit: any) => {
                if (unit.isAdded) {
                  unitIDs.push(unit.unitID);
                }
              });
          });
      });
    return { officeIDs: officeIDs, projectIDs: projectIDs, unitIDs: unitIDs };
  };

  const handleSubmit = async (movingData: any) => {
    const listOfIds = retriveIds();
    const data = {
      plans: [
        {
          planID: Number(planData?.planID),
          planReviewTypeID: 0,
        },
      ],
      officeIDs: listOfIds.officeIDs,
      projectIDs: listOfIds?.projectIDs,
      unitIDs: listOfIds?.unitIDs,
    };
    await mutateAsync({ data: data });
    onClosed();
  };

  const mergeData = (data: any) => {
    const mergedOffices: any = {};

    Array.isArray(data) &&
      data?.forEach((office: any) => {
        if (!mergedOffices[office.officeCode]) {
          mergedOffices[office.officeCode] = {
            ...office,
            planProjectMappings: [],
          };
        }

        Array.isArray(office.planProjectMappings) &&
          office.planProjectMappings?.forEach((project: any) => {
            const existingProject = mergedOffices[office.officeCode].planProjectMappings.find(
              (p: any) => p.projectID === project.projectID
            );

            if (existingProject) {
              existingProject.planUnitMappings.push(...project.planUnitMappings);
            } else {
              mergedOffices[office.officeCode].planProjectMappings.push({ ...project });
            }
          });
      });

    return Object.values(mergedOffices);
  };

  const handleMappedData = (data: any) => {
    const joinData: any = [...newMappedData ?? [], data?.planOfficeMappings[0]];
    const result: any[] = mergeData(joinData);
    setNewMappedData(result);
  };

  const updateMappedData = (data: any[]) => {
    setNewMappedData(data);
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog
      open={true}
      fullWidth={true}
      maxWidth={'xl'}
    // onClose={onClosed}
    >
      <DialogTitle id="scroll-dialog-title">Plan Map</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClosed}
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <ReactHookForm
        formName="MOVE_PLAN_FORM"
        onSubmit={handleSubmit}
        formContextProps={form}
        isBusy={isMovingPlanLoading}
      >
        <DialogContent dividers className="popupDilog">
          <Grid container spacing={2}>
            <Grid item xl={12} lg={12} sm={12} xs={12}>
              <Grid container spacing={2}>
                <Grid item xl={6} lg={6} sm={6} xs={12}>
                  <FormInput label="Plan (or File) Name" name="planName" disabled />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xl={12} lg={12} sm={12} xs={12}>
              <MovePlanComponent customerID={planData?.customerID} handleNewMappedData={handleMappedData} />
            </Grid>
            <Grid item xl={12} lg={12} sm={12} xs={12}>
              <PlanMapListComponent
                planID={planData?.planID}
                newMappedData={newMappedData}
                onUpdateMappedData={updateMappedData}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <div style={{ padding: '5px', paddingLeft: '16px', paddingRight: '16px' }}>
          <DialogActions className="spaceBetween">
            <Button variant="outlined" style={{ width: '130px' }} onClick={onClosed}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              style={{ width: '130px' }}
              disabled={isMovingPlanLoading || isReadOnlyPermission || newMappedData?.length === 0}
            >
              Save
            </Button>
          </DialogActions>
        </div>
      </ReactHookForm>
    </Dialog>
  );
};
